/**
 * Client-side mirror of one room's live state. WP4 only ever calls `hydrate` (after the snapshot
 * query resolves — see `routes/Room.tsx`), so every mutation still goes through TanStack Query's
 * refetch-and-hydrate cycle. The `apply*` methods exist now, gated by version, so WP5 can wire a
 * STOMP subscription straight into them without touching this file's shape: a message that arrives
 * out of order (replayed, or racing a refetch) is dropped rather than regressing the UI.
 */
import { create } from 'zustand';
import {
  cellKey,
  type FieldCell,
  type LockEntry,
  type LockTargetKind,
  type Member,
  type Reveal,
  type RoomSnapshot,
} from '../lib/rooms';
import type { OverlayRoom } from '../lib/overlay';

/** One `field_value` row as the (now people-free — see WP6) `fields` STOMP topic carries it: no
 * `updatedBy`. A `FieldCell` still has that field (the REST snapshot and the PUT-field mutation
 * response both carry it), so `applyFieldChange` accepts either shape and, for a broadcast lacking
 * `updatedBy`, keeps whatever the cell's last known `updatedBy` was rather than clobbering it with
 * `undefined`. */
export type FieldBroadcast = Omit<FieldCell, 'updatedBy'>;

/** A WP6-split `RoomChanged` broadcast: `room` is the WP6 people-free `OverlayRoom` shape (no
 * `createdBy`/`guestUserId`) wrapped with the `roomVersion` the mutation bumped to (`room` payloads
 * across every WP5/WP6 topic gate the same way — see `docs/specs/WP5-realtime.md`'s contract
 * table). `applyRoom` merges this into the existing snapshot room rather than replacing it, so
 * `createdBy`/`guestUserId` — absent from the broadcast — survive untouched; `members` is carried
 * on its own topic (see `MembersBroadcast`) so the Overlay principal (denied `members`) can still
 * subscribe `room`. */
export interface RoomBroadcast {
  room: OverlayRoom;
  roomVersion: number;
}

/** A WP6-split `MembersChanged` broadcast: the current member list and the room's current
 * `guestUserId` (a membership change can itself change who the guest is), wrapped with the
 * `roomVersion` the mutation bumped to (a member list alone carries no version to compare — same
 * reasoning as `SectionBroadcast`/`RevealBroadcast`). User principals only; the Overlay principal is
 * denied this topic. */
export interface MembersBroadcast {
  members: Member[];
  guestUserId: number | null;
  roomVersion: number;
}

/** A WP5 `SectionChanged` broadcast. Like `RevealBroadcast`, `sectionId`/`enteredAt` alone carry no
 * version (clearing the current section to `null` has nothing to compare), so the broadcast wraps
 * them with the `roomVersion` the mutation bumped to. */
export interface SectionBroadcast {
  sectionId: number | null;
  enteredAt: string | null;
  roomVersion: number;
}

/** A WP5 `RevealChanged` broadcast. Unlike `RoomChanged`, `Reveal`/`null` alone carries no version
 * (there's nothing to compare when hiding a reveal clears it to `null`), so the broadcast wraps it
 * with the `room.roomVersion` the mutation that produced it bumped to. */
export interface RevealBroadcast {
  reveal: Reveal | null;
  roomVersion: number;
}

interface RoomStoreState {
  snapshot: RoomSnapshot | null;
  fields: Map<string, FieldCell>;
  locks: LockEntry[];
  hydrate: (snapshot: RoomSnapshot) => void;
  applyFieldChange: (cell: FieldCell | FieldBroadcast) => void;
  applyLock: (lock: LockEntry) => void;
  applyUnlock: (targetKind: LockTargetKind, targetId: number) => void;
  applyRoom: (payload: RoomBroadcast) => void;
  applyMembers: (payload: MembersBroadcast) => void;
  applySection: (payload: SectionBroadcast) => void;
  applyReveal: (payload: RevealBroadcast) => void;
  reset: () => void;
}

function fieldsToMap(fields: FieldCell[]): Map<string, FieldCell> {
  const map = new Map<string, FieldCell>();
  for (const field of fields) {
    map.set(cellKey(field.fieldId, field.rowKind), field);
  }
  return map;
}

export const useRoomStore = create<RoomStoreState>((set, get) => ({
  snapshot: null,
  fields: new Map(),
  locks: [],

  hydrate: (snapshot) => {
    set({ snapshot, fields: fieldsToMap(snapshot.fields), locks: snapshot.locks });
  },

  applyFieldChange: (cell) => {
    const key = cellKey(cell.fieldId, cell.rowKind);
    const existing = get().fields.get(key);
    // Last-write-wins gate: a message whose version doesn't advance the local one is stale —
    // either replayed, or overtaken by a request that already landed.
    if (existing && cell.version <= existing.version) {
      return;
    }
    // A people-free `fields` broadcast has no `updatedBy` key at all (as opposed to carrying one
    // that's `null`) — keep the cell's last known `updatedBy` rather than clobbering it.
    const updatedBy = 'updatedBy' in cell ? cell.updatedBy : (existing?.updatedBy ?? null);
    const fields = new Map(get().fields);
    fields.set(key, { ...cell, updatedBy });
    set({ fields });
  },

  applyLock: (lock) => {
    const locks = get().locks.filter(
      (existing) => !(existing.targetKind === lock.targetKind && existing.targetId === lock.targetId),
    );
    locks.push(lock);
    set({ locks });
  },

  applyUnlock: (targetKind, targetId) => {
    set({
      locks: get().locks.filter((lock) => !(lock.targetKind === targetKind && lock.targetId === targetId)),
    });
  },

  applyRoom: ({ room, roomVersion }) => {
    const snapshot = get().snapshot;
    if (!snapshot) {
      return;
    }
    // Same last-write-wins gate as `applyFieldChange`: a `RoomChanged` broadcast that doesn't
    // advance `roomVersion` past the local one is stale — replayed, or overtaken by a request (or
    // another broadcast) that already landed — and must not regress the UI.
    if (roomVersion <= snapshot.room.roomVersion) {
      return;
    }
    // `room` is the WP6 people-free `OverlayRoom` shape — it has no `createdBy`/`guestUserId` keys
    // at all, so spreading it over the existing room leaves those two untouched rather than
    // wiping them to `undefined`.
    set({ snapshot: { ...snapshot, room: { ...snapshot.room, ...room, roomVersion } } });
  },

  applyMembers: ({ members, guestUserId, roomVersion }) => {
    const snapshot = get().snapshot;
    if (!snapshot) {
      return;
    }
    if (roomVersion <= snapshot.room.roomVersion) {
      return;
    }
    set({ snapshot: { ...snapshot, room: { ...snapshot.room, guestUserId, roomVersion }, members } });
  },

  applySection: ({ sectionId, roomVersion }) => {
    const snapshot = get().snapshot;
    if (!snapshot) {
      return;
    }
    if (roomVersion <= snapshot.room.roomVersion) {
      return;
    }
    set({ snapshot: { ...snapshot, room: { ...snapshot.room, currentSectionId: sectionId, roomVersion } } });
  },

  applyReveal: ({ reveal, roomVersion }) => {
    const snapshot = get().snapshot;
    if (!snapshot) {
      return;
    }
    if (roomVersion <= snapshot.room.roomVersion) {
      return;
    }
    set({ snapshot: { ...snapshot, room: { ...snapshot.room, activeReveal: reveal, roomVersion } } });
  },

  reset: () => set({ snapshot: null, fields: new Map(), locks: [] }),
}));
